import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatListModule } from '@angular/material/list';
import { MatDividerModule } from '@angular/material/divider';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBarModule, MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RentalService } from '../../../core/api/rental.service';
import { PaymentService } from '../../../core/api/payment.service';
import { Rental, RentalExtraService, RentalStatus } from '../../../core/models/rental.model';
import { Image } from '../../../core/models/image.model';
import { Payment } from '../../../core/models/payment.model';
import { PaymentCardComponent } from '../../payments/payment-card/payment-card.component';
import { RentalDeleteDialogComponent } from '../rental-delete-dialog/rental-delete-dialog.component';
import { StatusLabelPipe } from '../../../shared/pipes/status-label.pipe';
import { formatDateTime } from '../../../shared/utils/date.utils';
import { CopyTextComponent } from '../../../shared/components/copy-text/copy-text.component';

@Component({
  selector: 'app-rental-detail',
  standalone: true,
  imports: [CommonModule, RouterModule, MatCardModule, MatIconModule, MatButtonModule, MatChipsModule, MatListModule, MatDividerModule, MatDialogModule, MatSnackBarModule, MatProgressSpinnerModule, PaymentCardComponent, RentalDeleteDialogComponent, StatusLabelPipe, CopyTextComponent],
  templateUrl: './rental-detail.component.html',
  styleUrl: './rental-detail.component.scss',
})
export class RentalDetailComponent implements OnInit {
  loading = true;
  rental: Rental | null = null;
  paymentsLoading = false;
  payments: Payment[] = [];
  RentalStatus = RentalStatus;
  formatDateTime = formatDateTime;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private rentalService: RentalService,
    private paymentService: PaymentService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    if (!id) return;
    this.rentalService.get(id).subscribe({
      next: (r) => { this.rental = r; this.loading = false; this.loadPayments(id); },
      error: () => { this.loading = false; },
    });
  }

  private loadPayments(rentalId: number): void {
    this.paymentsLoading = true;
    this.paymentService.list(rentalId).subscribe({
      next: (res) => { this.payments = res.results || []; this.paymentsLoading = false; },
      error: () => { this.paymentsLoading = false; },
    });
  }

  onPaymentDeleted(): void {
    if (this.rental) {
      this.loadPayments(this.rental.id);
    }
  }

  deleteRental(): void {
    if (!this.rental) return;
    const ref: MatDialogRef<RentalDeleteDialogComponent, boolean> = this.dialog.open(
      RentalDeleteDialogComponent,
      { data: { rental: this.rental }, width: '420px' },
    );
    ref.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;
      this.rentalService.delete(this.rental!.id).subscribe({
        next: () => {
          this.snackBar.open('Прокат удалён', 'ОК', { duration: 3000 });
          this.router.navigate(['/rentals']);
        },
        error: () => {
          this.snackBar.open('Не удалось удалить прокат', 'ОК', { duration: 5000 });
        },
      });
    });
  }

  /**
   * Открывает детальное изображение в новой вкладке.
   * Используется из шаблона rental-detail (там нет CVA-компонента —
   * это read-only просмотр).
   */
  openImage(img: Image): void {
    if (img?.detail) {
      window.open(img.detail, '_blank', 'noopener,noreferrer');
    }
  }

  get paidPaymentsTotal(): number {
    return this.payments.reduce((sum, p) => {
      if (!p.is_paid) return sum;
      const value = Number(p.amount);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
  }

  /**
   * Сумма по всем доп. услугам проката. На бэке `amount` хранится как
   * decimal-строка (`"500.00"`), приводим к числу через `Number`.
   */
  get extraServicesTotal(): number {
    const list: RentalExtraService[] = this.rental?.extra_services ?? [];
    return list.reduce((sum, es) => {
      const value = Number(es.amount);
      return sum + (Number.isFinite(value) ? value : 0);
    }, 0);
  }
}
